import type { LocationQuery, RouteLocationRaw } from 'vue-router'

// The `?param` delegation registry (contract 05 §4/§8, ORP-18611) — the seam that lets the v3
// mega-`beforeEach` query branches (§3.1 rows 18–20, 23–27) live in their owning feature instead
// of in navigation. This module holds ZERO handlers: 06/07/08/11/12 contribute theirs on their
// own tickets. OAuth's one-time auth handoff is the measured query exception; provider-specific
// Shopify/Stripe returns use dedicated `blank` callback routes (see app/router/routes/callbacks.ts).
//
// Registration is app-side. The enforced layer rule (eslint `boundaries/dependencies`) forbids
// features from importing `app/`, so a feature cannot call `registerQueryCallback` itself; it
// exports a handler from its barrel and the composition root registers it — the same inversion
// `routes/index.ts` already uses for feature route modules. That also makes handlers eagerly
// available: a lazily-imported feature chunk could not self-register before the first navigation,
// which is exactly when a callback must fire.

export interface QueryCallbackContext {
  /** The route that received the param; lets an owner distinguish legacy overlap from its own callback page. */
  path: string
  /** The consumed param's value — `''` for a valueless `?param`, first value if repeated. */
  value: string
  /** The whole triggering query, so a handler can read its companions (`?success=1&message=…`). */
  query: LocationQuery
}

export interface QueryCallbackOutcome {
  /** Land here instead of the current route. The seam `replace`s (never pushes) to it. */
  redirect: RouteLocationRaw
}

export interface QueryCallbackPreserve {
  /** This owner deliberately declines the param on the current route; leave the URL untouched. */
  preserve: true
}

export type QueryCallbackHandler = (
  context: QueryCallbackContext,
) =>
  | void
  | QueryCallbackOutcome
  | QueryCallbackPreserve
  | Promise<void | QueryCallbackOutcome | QueryCallbackPreserve>

// Insertion-ordered: `useQueryCallbacks` runs matching handlers in REGISTRATION order and the
// first redirect wins, mirroring v3's top-to-bottom branches where `account_id`/`workspace_id`
// fall through and `report_id` short-circuits with `next()`. Registration order is therefore
// load-bearing and belongs in one app-side composition site.
const handlers = new Map<string, QueryCallbackHandler>()

/**
 * Bind `param` to its one owning feature handler. Throws on a second registration: contract §18
 * gives every callback exactly one owner, and a silent overwrite would make the winner depend on
 * module import order.
 */
export function registerQueryCallback(param: string, handler: QueryCallbackHandler): void {
  if (handlers.has(param)) {
    throw new Error(`registerQueryCallback: "${param}" already has an owner`)
  }
  handlers.set(param, handler)
}

/** The registered handlers in registration order (read by `useQueryCallbacks`). */
export function queryCallbackHandlers(): ReadonlyMap<string, QueryCallbackHandler> {
  return handlers
}

/** Drop every registration. Exists so tests start from an empty registry. */
export function resetQueryCallbacks(): void {
  handlers.clear()
}
